<script setup lang="ts">
import { useCamera } from '@/composables/useCamera';
// import { useSafeArea } from '@/composables/useSafeArea';
import type { CameraUploadPayload } from '@/types/camera';
import { Capacitor } from '@capacitor/core';
import Button from 'primevue/button';
import Sidebar from 'primevue/sidebar';
import type { ModelRef } from 'vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const visible: ModelRef<boolean> = defineModel('visible', { type: Boolean, required: true });
// const { bottomSafeArea } = useSafeArea();
const { t } = useI18n();
const { takePhoto, selectFromGallery } = useCamera();

const props = defineProps<{
  header: string;
  cameraText?: string;
  galleryText?: string;
  cameraAccept?: string; // new
  galleryAccept?: string; // new
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'file-uploaded', payload: CameraUploadPayload): void;
}>();

const cameraInputRef = ref<HTMLInputElement | null>(null);
const galleryInputRef = ref<HTMLInputElement | null>(null);

// Web input handlers
const openCameraInput = () => cameraInputRef.value?.click();
const openGalleryInput = () => galleryInputRef.value?.click();

const onCameraClick = async () => {
  if (Capacitor.isNativePlatform()) {
    const payload = await takePhoto();
    if (payload) {
      emit('file-uploaded', payload);
      emit('update:visible', false);
    }
  } else {
    openCameraInput();
  }
};

const onGalleryClick = async () => {
  if (Capacitor.isNativePlatform()) {
    const payload = await selectFromGallery();
    if (payload) {
      emit('file-uploaded', payload);
      emit('update:visible', false);
    }
  } else {
    openGalleryInput();
  }
};

// Web handlers → on crée le même payload qu'en mobile
const onCameraFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (!input.files?.length) return;

  const file = input.files[0];
  emit('file-uploaded', {
    type: 'camera',
    file,
    name: file.name,
    mimeType: file.type,
    size: file.size,
  });
  emit('update:visible', false);
  input.value = '';
};

const onGalleryFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (!input.files?.length) return;

  const file = input.files[0];
  emit('file-uploaded', {
    type: 'gallery',
    file,
    name: file.name,
    mimeType: file.type,
    size: file.size,
  });
  emit('update:visible', false);
  input.value = ''; // reset like camera
};
</script>

<template>
  <Sidebar v-model:visible="visible" :header="header" position="bottom" style="height: auto" :block-scroll="true" class="w-full md:w-3/4 lg:w-1/2">
    <div class="my-2 flex items-center justify-evenly gap-2 py-5">
      <div class="flex flex-col items-center gap-2">
        <Button rounded style="width: 60px; height: 60px" @click="onCameraClick">
          <span class="pi pi-camera text-xl"></span>
        </Button>
        <p class="text-center text-xs">{{ props.cameraText || t('common.camera') }}</p>
      </div>
      <div class="flex flex-col items-center gap-2">
        <Button rounded style="width: 60px; height: 60px" @click="onGalleryClick">
          <span class="pi pi-images text-xl"></span>
        </Button>
        <p class="text-center text-xs">{{ props.galleryText || t('common.gallery') }}</p>
      </div>
    </div>

    <!-- Hidden file inputs for web -->
    <input
v-if="!Capacitor.isNativePlatform()" ref="cameraInputRef" type="file"
      :accept="props.cameraAccept || 'image/*'" capture="environment" class="hidden" @change="onCameraFileChange" />
    <input
v-if="!Capacitor.isNativePlatform()" ref="galleryInputRef" type="file"
      :accept="props.galleryAccept || 'image/*'" class="hidden" @change="onGalleryFileChange" />
  </Sidebar>
</template>
